// path: frontend/src/lib/darkMode.ts
//--------------------------------------------------------------
// Robust dark-mode controller (Tailwind v4 uses `dark` class)
// - Works in Safari (old addListener API) and modern browsers
// - Persists explicit user choice; otherwise follows system
// - Exposes helpers for forcing/reverting + data-theme reflect
//--------------------------------------------------------------

const STORAGE_KEY = "prefers-dark"; // "true" | "false" | null
type RootEl = HTMLElement | undefined;

/** True if we're in a browser (not SSR) */
const hasDOM = typeof window !== "undefined" && typeof document !== "undefined";

function getRoot(root?: RootEl) {
  return (root ?? (hasDOM ? document.documentElement : undefined))!;
}

/** Reflect current theme for libs that check attributes instead of classes. */
export function reflectDataTheme(root?: RootEl) {
  if (!hasDOM) return;
  const r = getRoot(root);
  r.setAttribute("data-theme", r.classList.contains("dark") ? "dark" : "light");
}

function setClass(enabled: boolean, root?: RootEl) {
  if (!hasDOM) return;
  const r = getRoot(root);
  r.classList.toggle("dark", enabled);
  reflectDataTheme(r); // keep data-theme in sync
}

function getStored(): "true" | "false" | null {
  if (!hasDOM) return null;
  return localStorage.getItem(STORAGE_KEY) as any;
}

function setStored(val: boolean | null) {
  if (!hasDOM) return;
  if (val === null) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, String(val));
}

function getSystemPref(): boolean {
  if (!hasDOM) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Initialise dark mode *before* first render.
 * Returns an unsubscribe function for the system-theme listener.
 */
export function initDarkMode(root?: RootEl): () => void {
  if (!hasDOM) return () => {};

  const saved = getStored(); // "true" | "false" | null
  const enabled = saved === "true" || (saved === null && getSystemPref());
  setClass(enabled, root);

  // Follow system changes only if the user hasn’t chosen explicitly.
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = (e: MediaQueryListEvent | MediaQueryList) => {
    if (getStored() === null) {
      const matches = "matches" in e ? e.matches : (e as MediaQueryList).matches;
      setClass(matches, root);
    }
  };

  // Modern + Safari fallback
  if ("addEventListener" in mql) {
    mql.addEventListener("change", onChange as any);
    return () => mql.removeEventListener("change", onChange as any);
  } else {
    // @ts-ignore legacy Safari
    mql.addListener(onChange);
    // @ts-ignore legacy Safari
    return () => mql.removeListener(onChange);
  }
}

/** Toggle and persist user choice. Returns the new state. */
export function toggleDarkMode(root?: RootEl): boolean {
  if (!hasDOM) return false;
  const html = getRoot(root);
  const enabled = !html.classList.contains("dark");
  setClass(enabled, root);
  setStored(enabled);
  return enabled;
}

/** Force a mode (true=dark / false=light) and persist. */
export function setDarkMode(enabled: boolean, root?: RootEl) {
  setClass(enabled, root);
  setStored(enabled);
}

/** Revert to following the OS theme (clears stored preference). */
export function followSystem(root?: RootEl) {
  setStored(null);
  setClass(getSystemPref(), root);
}

/** Read current effective mode. */
export function isDarkMode(root?: RootEl): boolean {
  if (!hasDOM) return false;
  return getRoot(root).classList.contains("dark");
}

